import React, { useState, useEffect } from 'react';
import { 
  getGlobalInclusions, 
  getGlobalExclusions, 
  createGlobalInclusion, 
  createGlobalExclusion, 
  deleteGlobalInclusion, 
  deleteGlobalExclusion,
  updateGlobalInclusion,
  updateGlobalExclusion,
  updateGlobalInclusionsOrder,
  updateGlobalExclusionsOrder
} from '../../services/globalListsService';
import Loader from '../../components/common/Loader';
import { PlusIcon, TrashIcon, ClipboardDocumentListIcon, PencilIcon, CheckIcon, XMarkIcon, Bars3Icon } from '@heroicons/react/24/outline';

function PkgInclusionExclusionPage() {
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Inclusion/Exclusion data
  const [inclusions, setInclusions] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  const [newInclusion, setNewInclusion] = useState('');
  const [newExclusion, setNewExclusion] = useState('');
  
  // Edit states
  const [editingInclusion, setEditingInclusion] = useState(null);
  const [editingExclusion, setEditingExclusion] = useState(null);
  const [editInclusionText, setEditInclusionText] = useState('');
  const [editExclusionText, setEditExclusionText] = useState('');
  
  // Drag and drop states
  const [draggedInclusion, setDraggedInclusion] = useState(null);
  const [draggedExclusion, setDraggedExclusion] = useState(null);
  
  // Search input states (separate from filters to prevent immediate API calls)
  const [inclusionSearchInput, setInclusionSearchInput] = useState('');
  const [exclusionSearchInput, setExclusionSearchInput] = useState('');
  
  // Search and pagination for inclusions
  const [inclusionFilters, setInclusionFilters] = useState({
    search: '',
    sort_by: 'display_order',
    sort_order: 'asc'
  });
  const [inclusionPagination, setInclusionPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0
  });
  
  // Search and pagination for exclusions
  const [exclusionFilters, setExclusionFilters] = useState({
    search: '',
    sort_by: 'display_order',
    sort_order: 'asc'
  });
  const [exclusionPagination, setExclusionPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0
  });

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Load both inclusions and exclusions
  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      await Promise.all([
        loadInclusions(),
        loadExclusions()
      ]);
    } catch (err) {
      console.error('❌ Failed to load data:', err);
      setError('Failed to load inclusion/exclusion lists');
    } finally {
      setLoading(false);
    }
  };

  // Load inclusions with current filters and pagination
  const loadInclusions = async () => {
    try {
      const response = await getGlobalInclusions(
        inclusionFilters, 
        inclusionPagination.page, 
        inclusionPagination.per_page
      );
      
      console.log('📋 Inclusions loaded:', response);
      
      setInclusions(response?.data || []);
      if (response?.meta?.pagination) {
        setInclusionPagination(prev => ({
          ...prev,
          total: response.meta.pagination.total || 0,
          pages: response.meta.pagination.total_pages || 0
        }));
      }
    } catch (error) {
      console.error('❌ Failed to load inclusions:', error);
      setInclusions([]);
    }
  };

  // Load exclusions with current filters and pagination
  const loadExclusions = async () => {
    try {
      const response = await getGlobalExclusions(
        exclusionFilters, 
        exclusionPagination.page, 
        exclusionPagination.per_page
      );
      
      console.log('📋 Exclusions loaded:', response);
      
      setExclusions(response?.data || []);
      if (response?.meta?.pagination) {
        setExclusionPagination(prev => ({
          ...prev,
          total: response.meta.pagination.total || 0,
          pages: response.meta.pagination.total_pages || 0
        }));
      }
    } catch (error) {
      console.error('❌ Failed to load exclusions:', error);
      setExclusions([]);
    }
  };

  // Inclusion management functions
  const addInclusion = async () => {
    if (!newInclusion.trim()) return;
    
    // Check if inclusion already exists
    if (inclusions.some(inc => inc.inclusion_text === newInclusion.trim())) {
      alert('This inclusion already exists!');
      return;
    }
    
    try {
      const inclusionData = {
        inclusion_text: newInclusion.trim(),
        display_order: inclusions.length
      };
      
      const response = await createGlobalInclusion(inclusionData);
      console.log('✅ Inclusion created:', response);
      
      // Reload inclusions to get updated list with proper pagination
      await loadInclusions();
      setNewInclusion('');
      
    } catch (error) {
      console.error('❌ Failed to create inclusion:', error);
      alert('Failed to add inclusion. Please try again.');
    }
  };

  const removeInclusion = async (inclusion) => {
    if (!window.confirm(`Are you sure you want to delete "${inclusion.inclusion_text}"?`)) {
      return;
    }
    
    try {
      await deleteGlobalInclusion(inclusion.id);
      console.log('✅ Inclusion deleted:', inclusion.id);
      
      // Reload inclusions to get updated list
      await loadInclusions();
      
    } catch (error) {
      console.error('❌ Failed to delete inclusion:', error);
      alert('Failed to delete inclusion. Please try again.');
    }
  };

  // Edit functions for inclusions
  const startEditInclusion = (inclusion) => {
    setEditingInclusion(inclusion.id);
    setEditInclusionText(inclusion.inclusion_text);
  };

  const cancelEditInclusion = () => {
    setEditingInclusion(null);
    setEditInclusionText('');
  };

  const saveEditInclusion = async () => {
    if (!editInclusionText.trim()) {
      alert('Inclusion text cannot be empty');
      return;
    }

    // Check if text is unchanged
    const currentInclusion = inclusions.find(inc => inc.id === editingInclusion);
    if (currentInclusion && editInclusionText.trim() === currentInclusion.inclusion_text) {
      cancelEditInclusion();
      return;
    }

    // Check for duplicates
    if (inclusions.some(inc => inc.id !== editingInclusion && inc.inclusion_text === editInclusionText.trim())) {
      alert('This inclusion already exists!');
      return;
    }

    try {
      const updateData = {
        inclusion_text: editInclusionText.trim()
      };
      
      await updateGlobalInclusion(editingInclusion, updateData);
      console.log('✅ Inclusion updated:', editingInclusion);
      
      // Reload inclusions to get updated list
      await loadInclusions();
      cancelEditInclusion();
      
    } catch (error) {
      console.error('❌ Failed to update inclusion:', error);
      alert('Failed to update inclusion. Please try again.');
    }
  };

  // Edit functions for exclusions
  const startEditExclusion = (exclusion) => {
    setEditingExclusion(exclusion.id);
    setEditExclusionText(exclusion.exclusion_text);
  };

  const cancelEditExclusion = () => {
    setEditingExclusion(null);
    setEditExclusionText('');
  };

  const saveEditExclusion = async () => {
    if (!editExclusionText.trim()) {
      alert('Exclusion text cannot be empty');
      return;
    }

    // Check if text is unchanged
    const currentExclusion = exclusions.find(exc => exc.id === editingExclusion);
    if (currentExclusion && editExclusionText.trim() === currentExclusion.exclusion_text) {
      cancelEditExclusion();
      return;
    }

    // Check for duplicates
    if (exclusions.some(exc => exc.id !== editingExclusion && exc.exclusion_text === editExclusionText.trim())) {
      alert('This exclusion already exists!');
      return;
    }

    try {
      const updateData = {
        exclusion_text: editExclusionText.trim()
      };
      
      await updateGlobalExclusion(editingExclusion, updateData);
      console.log('✅ Exclusion updated:', editingExclusion);
      
      // Reload exclusions to get updated list
      await loadExclusions();
      cancelEditExclusion();
      
    } catch (error) {
      console.error('❌ Failed to update exclusion:', error);
      alert('Failed to update exclusion. Please try again.');
    }
  };

  // Exclusion management functions
  const addExclusion = async () => {
    if (!newExclusion.trim()) return;
    
    // Check if exclusion already exists
    if (exclusions.some(exc => exc.exclusion_text === newExclusion.trim())) {
      alert('This exclusion already exists!');
      return;
    }
    
    try {
      const exclusionData = {
        exclusion_text: newExclusion.trim(),
        display_order: exclusions.length
      };
      
      const response = await createGlobalExclusion(exclusionData);
      console.log('✅ Exclusion created:', response);
      
      // Reload exclusions to get updated list with proper pagination
      await loadExclusions();
      setNewExclusion('');
      
    } catch (error) {
      console.error('❌ Failed to create exclusion:', error);
      alert('Failed to add exclusion. Please try again.');
    }
  };

  const removeExclusion = async (exclusion) => {
    if (!window.confirm(`Are you sure you want to delete "${exclusion.exclusion_text}"?`)) {
      return;
    }
    
    try {
      await deleteGlobalExclusion(exclusion.id);
      console.log('✅ Exclusion deleted:', exclusion.id);
      
      // Reload exclusions to get updated list
      await loadExclusions();
      
    } catch (error) {
      console.error('❌ Failed to delete exclusion:', error);
      alert('Failed to delete exclusion. Please try again.');
    }
  };

  // Handle search for inclusions (only updates the input, not the filter)
  const handleInclusionSearchInput = (searchValue) => {
    setInclusionSearchInput(searchValue);
  };

  // Handle search for exclusions (only updates the input, not the filter)
  const handleExclusionSearchInput = (searchValue) => {
    setExclusionSearchInput(searchValue);
  };

  // Perform actual search when Enter is pressed or search is triggered
  const performInclusionSearch = () => {
    setInclusionFilters(prev => ({ ...prev, search: inclusionSearchInput }));
    setInclusionPagination(prev => ({ ...prev, page: 1 }));
  };

  const performExclusionSearch = () => {
    setExclusionFilters(prev => ({ ...prev, search: exclusionSearchInput }));
    setExclusionPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle Enter key press for search
  const handleInclusionSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      performInclusionSearch();
    }
  };

  const handleExclusionSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      performExclusionSearch();
    }
  };

  // Effect to reload inclusions when filters change
  useEffect(() => {
    if (!loading) {
      // Add a small delay to prevent race conditions during rapid state changes
      const timeoutId = setTimeout(() => {
        loadInclusions();
      }, 10);
      
      return () => clearTimeout(timeoutId);
    }
  }, [inclusionFilters, inclusionPagination.page]);

  // Effect to reload exclusions when filters change
  useEffect(() => {
    if (!loading) {
      // Add a small delay to prevent race conditions during rapid state changes
      const timeoutId = setTimeout(() => {
        loadExclusions();
      }, 10);
      
      return () => clearTimeout(timeoutId);
    }
  }, [exclusionFilters, exclusionPagination.page]);

  // Handle pagination for inclusions
  const handleInclusionPageChange = (newPage) => {
    setInclusionPagination(prev => ({ ...prev, page: newPage }));
  };

  // Handle pagination for exclusions
  const handleExclusionPageChange = (newPage) => {
    setExclusionPagination(prev => ({ ...prev, page: newPage }));
  };

  // Drag and drop handlers for inclusions
  const handleInclusionDragStart = (e, inclusion) => {
    setDraggedInclusion(inclusion);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleInclusionDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleInclusionDrop = async (e, targetInclusion) => {
    e.preventDefault();
    
    if (!draggedInclusion || draggedInclusion.id === targetInclusion.id) {
      setDraggedInclusion(null);
      return;
    }

    // Calculate new order
    const draggedIndex = inclusions.findIndex(inc => inc.id === draggedInclusion.id);
    const targetIndex = inclusions.findIndex(inc => inc.id === targetInclusion.id);
    
    const newInclusions = [...inclusions];
    const [removed] = newInclusions.splice(draggedIndex, 1);
    newInclusions.splice(targetIndex, 0, removed);
    
    // Update display_order for all items
    const orderUpdates = newInclusions.map((inclusion, index) => ({
      id: inclusion.id,
      display_order: index
    }));
    
    try {
      await updateGlobalInclusionsOrder(orderUpdates);
      console.log('✅ Inclusions order updated');
      
      // Reload inclusions to get updated order
      await loadInclusions();
    } catch (error) {
      console.error('❌ Failed to update inclusions order:', error);
      alert('Failed to update order. Please try again.');
    }
    
    setDraggedInclusion(null);
  };

  const handleInclusionDragEnd = () => {
    setDraggedInclusion(null);
  };

  // Drag and drop handlers for exclusions
  const handleExclusionDragStart = (e, exclusion) => {
    setDraggedExclusion(exclusion);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleExclusionDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleExclusionDrop = async (e, targetExclusion) => {
    e.preventDefault();
    
    if (!draggedExclusion || draggedExclusion.id === targetExclusion.id) {
      setDraggedExclusion(null);
      return;
    }

    // Calculate new order
    const draggedIndex = exclusions.findIndex(exc => exc.id === draggedExclusion.id);
    const targetIndex = exclusions.findIndex(exc => exc.id === targetExclusion.id);
    
    const newExclusions = [...exclusions];
    const [removed] = newExclusions.splice(draggedIndex, 1);
    newExclusions.splice(targetIndex, 0, removed);
    
    // Update display_order for all items
    const orderUpdates = newExclusions.map((exclusion, index) => ({
      id: exclusion.id,
      display_order: index
    }));
    
    try {
      await updateGlobalExclusionsOrder(orderUpdates);
      console.log('✅ Exclusions order updated');
      
      // Reload exclusions to get updated order
      await loadExclusions();
    } catch (error) {
      console.error('❌ Failed to update exclusions order:', error);
      alert('Failed to update order. Please try again.');
    }
    
    setDraggedExclusion(null);
  };

  const handleExclusionDragEnd = () => {
    setDraggedExclusion(null);
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="p-4 bg-gray-50 min-h-screen">
        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-xl shadow-lg" role="alert">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
            <span className="block sm:inline font-medium">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 bg-gray-50 h-screen overflow-hidden">
      {/* Page Header */}
      <div className="mb-3">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <ClipboardDocumentListIcon className="h-6 w-6 text-indigo-600 mr-2" />
          Package Inclusion & Exclusion Lists
        </h1>
        <p className="text-gray-600 text-sm mt-1">
          Manage global inclusion and exclusion items that can be used across all packages.
        </p>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 flex-1 overflow-hidden">
        
        {/* Inclusions Section */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300 flex flex-col overflow-hidden">
          <div className="px-4 py-2 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200 rounded-t-xl flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
              Inclusions ({inclusionPagination.total})
            </h2>
            <p className="text-xs text-gray-600">
              Services and features included in packages • Drag to reorder
            </p>
          </div>
          
          <div className="p-3 flex-1 flex flex-col overflow-hidden">
            {/* Search for Inclusions */}
            <div className="mb-4">
              <div className="flex">
                <input
                  type="text"
                  placeholder="Search inclusions... (Press Enter to search)"
                  value={inclusionSearchInput}
                  onChange={(e) => handleInclusionSearchInput(e.target.value)}
                  onKeyPress={handleInclusionSearchKeyPress}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                />
                <button
                  onClick={performInclusionSearch}
                  className="px-4 py-2 bg-green-500 text-white rounded-r-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-200 text-sm"
                >
                  Search
                </button>
              </div>
              {inclusionFilters.search && (
                <div className="mt-2 flex items-center justify-between text-sm text-gray-600">
                  <span>Searching for: "{inclusionFilters.search}"</span>
                  <button
                    onClick={async () => {
                      // Clear all search states first
                      setInclusionFilters(prev => ({ ...prev, search: '' }));
                      setInclusionSearchInput('');
                      setInclusionPagination(prev => ({ ...prev, page: 1 }));
                      
                      // Manually reload with empty search to avoid double API calls
                      try {
                        const response = await getGlobalInclusions(
                          { ...inclusionFilters, search: '' }, 
                          1, 
                          inclusionPagination.per_page
                        );
                        
                        setInclusions(response?.data || []);
                        if (response?.meta?.pagination) {
                          setInclusionPagination(prev => ({
                            ...prev,
                            total: response.meta.pagination.total || 0,
                            pages: response.meta.pagination.total_pages || 0,
                            page: 1
                          }));
                        }
                      } catch (error) {
                        console.error('❌ Failed to load inclusions:', error);
                      }
                    }}
                    className="text-red-500 hover:text-red-700 underline"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>

            {/* Add New Inclusion */}
            <div className="flex mb-3">
              <input
                type="text"
                value={newInclusion}
                onChange={(e) => setNewInclusion(e.target.value)}
                placeholder="Add new inclusion..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 text-sm"
                onKeyPress={(e) => e.key === 'Enter' && addInclusion()}
              />
              <button
                onClick={addInclusion}
                className="px-3 py-2 bg-green-500 text-white rounded-r hover:bg-green-600 focus:outline-none focus:ring-1 focus:ring-green-500 transition-colors duration-200"
              >
                <PlusIcon className="h-3 w-3" />
              </button>
            </div>

            {/* Inclusions List */}
            <div className="space-y-1 flex-1 overflow-y-auto">
              {inclusions.map((inclusion) => (
                <div 
                  key={inclusion.id} 
                  draggable={!editingInclusion}
                  onDragStart={(e) => handleInclusionDragStart(e, inclusion)}
                  onDragOver={handleInclusionDragOver}
                  onDrop={(e) => handleInclusionDrop(e, inclusion)}
                  onDragEnd={handleInclusionDragEnd}
                  className={`flex items-center justify-between bg-gray-50 p-2 rounded border border-green-200 hover:bg-green-50 transition-colors duration-150 ${
                    draggedInclusion?.id === inclusion.id ? 'opacity-50' : ''
                  } ${editingInclusion ? 'cursor-default' : 'cursor-move'}`}
                >
                  {editingInclusion === inclusion.id ? (
                    // Edit mode
                    <>
                      <div className="flex items-center flex-1">
                        <div className="w-1 h-1 bg-green-400 rounded-full mr-2 flex-shrink-0"></div>
                        <input
                          type="text"
                          value={editInclusionText}
                          onChange={(e) => setEditInclusionText(e.target.value)}
                          className="flex-1 px-2 py-1 text-sm border border-green-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') saveEditInclusion();
                            if (e.key === 'Escape') cancelEditInclusion();
                          }}
                          autoFocus
                        />
                      </div>
                      <div className="flex items-center space-x-1 ml-2">
                        <button
                          onClick={saveEditInclusion}
                          className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-100 transition-colors duration-150"
                          title="Save changes"
                        >
                          <CheckIcon className="h-3 w-3" />
                        </button>
                        <button
                          onClick={cancelEditInclusion}
                          className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100 transition-colors duration-150"
                          title="Cancel editing"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </>
                  ) : (
                    // View mode
                    <>
                      <div className="flex items-center flex-1">
                        {!editingInclusion && (
                          <Bars3Icon className="h-3 w-3 text-gray-400 mr-2 flex-shrink-0 cursor-move" />
                        )}
                        <span className="text-sm text-gray-700 flex items-center flex-1">
                          <div className="w-1 h-1 bg-green-400 rounded-full mr-2 flex-shrink-0"></div>
                          {inclusion.inclusion_text}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1 ml-2">
                        <button
                          onClick={() => startEditInclusion(inclusion)}
                          className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colors duration-150"
                          title="Edit inclusion"
                        >
                          <PencilIcon className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => removeInclusion(inclusion)}
                          className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors duration-150"
                          title="Delete inclusion"
                        >
                          <TrashIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              
              {inclusions.length === 0 && (
                <div className="text-sm text-gray-500 text-center py-8">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <PlusIcon className="w-6 h-6 text-green-400" />
                  </div>
                  {inclusionFilters.search ? 'No inclusions found for your search' : 'No inclusions added yet'}
                </div>
              )}
            </div>

            {/* Inclusions Pagination */}
            {inclusionPagination.pages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing {((inclusionPagination.page - 1) * inclusionPagination.per_page) + 1} to {Math.min(inclusionPagination.page * inclusionPagination.per_page, inclusionPagination.total)} of {inclusionPagination.total} inclusions
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleInclusionPageChange(inclusionPagination.page - 1)}
                    disabled={inclusionPagination.page <= 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handleInclusionPageChange(inclusionPagination.page + 1)}
                    disabled={inclusionPagination.page >= inclusionPagination.pages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Exclusions Section */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300">
          <div className="px-4 py-2 bg-gradient-to-r from-red-50 to-pink-50 border-b border-red-200 rounded-t-xl">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
              Exclusions ({exclusionPagination.total})
            </h2>
            <p className="text-xs text-gray-600">
              Services and features not included in packages • Drag to reorder
            </p>
          </div>
          
          <div className="p-3">
            {/* Search for Exclusions */}
            <div className="mb-4">
              <div className="flex">
                <input
                  type="text"
                  placeholder="Search exclusions... (Press Enter to search)"
                  value={exclusionSearchInput}
                  onChange={(e) => handleExclusionSearchInput(e.target.value)}
                  onKeyPress={handleExclusionSearchKeyPress}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-l focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 text-sm"
                />
                <button
                  onClick={performExclusionSearch}
                  className="px-3 py-2 bg-red-500 text-white rounded-r hover:bg-red-600 focus:outline-none focus:ring-1 focus:ring-red-500 transition-colors duration-200 text-sm"
                >
                  Search
                </button>
              </div>
              {exclusionFilters.search && (
                <div className="mt-2 flex items-center justify-between text-sm text-gray-600">
                  <span>Searching for: "{exclusionFilters.search}"</span>
                  <button
                    onClick={async () => {
                      // Clear all search states first
                      setExclusionFilters(prev => ({ ...prev, search: '' }));
                      setExclusionSearchInput('');
                      setExclusionPagination(prev => ({ ...prev, page: 1 }));
                      
                      // Manually reload with empty search to avoid double API calls
                      try {
                        const response = await getGlobalExclusions(
                          { ...exclusionFilters, search: '' }, 
                          1, 
                          exclusionPagination.per_page
                        );
                        
                        setExclusions(response?.data || []);
                        if (response?.meta?.pagination) {
                          setExclusionPagination(prev => ({
                            ...prev,
                            total: response.meta.pagination.total || 0,
                            pages: response.meta.pagination.total_pages || 0,
                            page: 1
                          }));
                        }
                      } catch (error) {
                        console.error('❌ Failed to load exclusions:', error);
                      }
                    }}
                    className="text-red-500 hover:text-red-700 underline"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>

            {/* Add New Exclusion */}
            <div className="flex mb-3">
              <input
                type="text"
                value={newExclusion}
                onChange={(e) => setNewExclusion(e.target.value)}
                placeholder="Add new exclusion..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 text-sm"
                onKeyPress={(e) => e.key === 'Enter' && addExclusion()}
              />
              <button
                onClick={addExclusion}
                className="px-3 py-2 bg-red-500 text-white rounded-r hover:bg-red-600 focus:outline-none focus:ring-1 focus:ring-red-500 transition-colors duration-200"
              >
                <PlusIcon className="h-3 w-3" />
              </button>
            </div>

            {/* Exclusions List */}
            <div className="space-y-1 flex-1 overflow-y-auto">
              {exclusions.map((exclusion) => (
                <div 
                  key={exclusion.id} 
                  draggable={!editingExclusion}
                  onDragStart={(e) => handleExclusionDragStart(e, exclusion)}
                  onDragOver={handleExclusionDragOver}
                  onDrop={(e) => handleExclusionDrop(e, exclusion)}
                  onDragEnd={handleExclusionDragEnd}
                  className={`flex items-center justify-between bg-gray-50 p-2 rounded border border-red-200 hover:bg-red-50 transition-colors duration-150 ${
                    draggedExclusion?.id === exclusion.id ? 'opacity-50' : ''
                  } ${editingExclusion ? 'cursor-default' : 'cursor-move'}`}
                >
                  {editingExclusion === exclusion.id ? (
                    // Edit mode
                    <>
                      <div className="flex items-center flex-1">
                        <div className="w-1 h-1 bg-red-400 rounded-full mr-2 flex-shrink-0"></div>
                        <input
                          type="text"
                          value={editExclusionText}
                          onChange={(e) => setEditExclusionText(e.target.value)}
                          className="flex-1 px-2 py-1 text-sm border border-red-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') saveEditExclusion();
                            if (e.key === 'Escape') cancelEditExclusion();
                          }}
                          autoFocus
                        />
                      </div>
                      <div className="flex items-center space-x-1 ml-2">
                        <button
                          onClick={saveEditExclusion}
                          className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-100 transition-colors duration-150"
                          title="Save changes"
                        >
                          <CheckIcon className="h-3 w-3" />
                        </button>
                        <button
                          onClick={cancelEditExclusion}
                          className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100 transition-colors duration-150"
                          title="Cancel editing"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </>
                  ) : (
                    // View mode
                    <>
                      <div className="flex items-center flex-1">
                        {!editingExclusion && (
                          <Bars3Icon className="h-3 w-3 text-gray-400 mr-2 flex-shrink-0 cursor-move" />
                        )}
                        <span className="text-sm text-gray-700 flex items-center flex-1">
                          <div className="w-1 h-1 bg-red-400 rounded-full mr-2 flex-shrink-0"></div>
                          {exclusion.exclusion_text}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1 ml-2">
                        <button
                          onClick={() => startEditExclusion(exclusion)}
                          className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colors duration-150"
                          title="Edit exclusion"
                        >
                          <PencilIcon className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => removeExclusion(exclusion)}
                          className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors duration-150"
                          title="Delete exclusion"
                        >
                          <TrashIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              
              {exclusions.length === 0 && (
                <div className="text-sm text-gray-500 text-center py-8">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <PlusIcon className="w-6 h-6 text-red-400" />
                  </div>
                  {exclusionFilters.search ? 'No exclusions found for your search' : 'No exclusions added yet'}
                </div>
              )}
            </div>

            {/* Exclusions Pagination */}
            {exclusionPagination.pages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing {((exclusionPagination.page - 1) * exclusionPagination.per_page) + 1} to {Math.min(exclusionPagination.page * exclusionPagination.per_page, exclusionPagination.total)} of {exclusionPagination.total} exclusions
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleExclusionPageChange(exclusionPagination.page - 1)}
                    disabled={exclusionPagination.page <= 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handleExclusionPageChange(exclusionPagination.page + 1)}
                    disabled={exclusionPagination.page >= exclusionPagination.pages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PkgInclusionExclusionPage;
